/**
 * Tenant + member request/response shapes.
 *
 * Endpoints (per IMPLEMENTATION.md §9.1):
 *   GET    /api/v1/me                       → MeResponse
 *   GET    /api/v1/tenants/current          → TenantResponse
 *   PATCH  /api/v1/tenants/current          → TenantResponse
 *   GET    /api/v1/tenants/current/members  → paginated MemberResponse
 */
import { z } from "zod";
import { IsoTimestampSchema, TenantSlugSchema, UuidSchema } from "./common.js";
/**
 * Tenant role at the API surface — coarse-grained for Phase 1; finer-grained
 * roles come later.
 *
 * Per ADR-015, the DB-side `tenant_members.role` column carries four values
 * (`owner | admin | operator | viewer`). The API surface projects those down
 * to three (`owner | admin | member`) at the boundary via
 * `mapDbRoleToApiRole()` in `role-mapping.ts`. Frontend treats `member` as
 * the catch-all "non-admin participant" role.
 *
 * Do NOT add `operator` or `viewer` here without updating ADR-015 and the
 * mapping helper. Do NOT remove values without coordinating with the DB
 * schema (`packages/db/src/schema/tenants.ts`) and RLY-210's CHECK
 * constraint.
 */
export const TenantRoleSchema = z.enum(["owner", "admin", "member"]);
/** Tenant record as returned by the API. Mirrors `tenants` row from RLY-009. */
export const TenantResponseSchema = z.object({
    id: UuidSchema,
    slug: TenantSlugSchema,
    name: z.string().min(1).max(120),
    created_at: IsoTimestampSchema,
});
/** PATCH /api/v1/tenants/current body. Only mutable fields. */
export const UpdateTenantRequestSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    slug: TenantSlugSchema.optional(),
})
    .refine((v) => v.name !== undefined || v.slug !== undefined, {
    message: "At least one of `name` or `slug` is required",
});
/** Currently-authenticated user. */
export const UserResponseSchema = z.object({
    id: UuidSchema,
    email: z.string().email(),
    display_name: z.string().min(1).max(120).nullable(),
});
/**
 * GET /api/v1/me — combined user + active tenant + role. The frontend depends
 * on this shape to bootstrap its session context (see ADR-005).
 */
export const MeResponseSchema = z.object({
    user: UserResponseSchema,
    tenant: TenantResponseSchema,
    role: TenantRoleSchema,
});
/** Single tenant member row. */
export const MemberResponseSchema = z.object({
    user_id: UuidSchema,
    email: z.string().email(),
    display_name: z.string().min(1).max(120).nullable(),
    role: TenantRoleSchema,
    joined_at: IsoTimestampSchema,
});
//# sourceMappingURL=tenants.js.map